<template>
  <WdsStack gap-sm direction-column>
    <WdsSelect
      v-model="internalSelection"
      v-model:search="searchStr"
      class="tw-min-w-[150px]"
      can-deselect
      multi-select
      searchable
      :data-test="`tc-filter-invited-by`"
      :placeholder="$t('All')"
      :label="meta.filter.quickFilter ? '' : $t('Added/Invited by')"
      :list="invitedByOptions"
      labels-primary
    >
      <template #option="{ option }">
        <div :data-test="`filter-invited-by-${option.label.replace(' ', '-').toLowerCase()}`">
          {{ option.label }}
        </div>
      </template>
    </WdsSelect>
  </WdsStack>
</template>

<script setup lang="ts">
import { WdsSelect, WdsStack } from '@worksome/wds-components'
import { computed, ModelRef, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { $t } from '@/composables/translation'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'
import { graphql } from '@/gql'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'

const { meta } = defineProps<{ meta: FilterMetaDataProp }>()

const searchStr = ref('')

// The model will be an InvitedByInput object: { users: [ID!]!, includeAutomation: Boolean! }
const selectedInvitedBy: ModelRef<{ users: string[]; includeAutomation: boolean } | null> =
  defineModel({
    default: () => null,
  })

// Fetch trusted contacts to get the list of users who have invited contacts
const TrustedContactsInvitedByFilterQuery = graphql(`
  query TrustedContactsInvitedByFilterQuery($accounts: [ID!], $first: Int!, $page: Int!) {
    trustedContacts(accounts: $accounts, first: $first, page: $page) {
      data {
        id
        invitedByUser {
          id
          name
        }
      }
    }
  }
`)

const { result, loading } = useQuery(
  TrustedContactsInvitedByFilterQuery as any,
  { accounts: [currentAccount.globalId], first: PER_PAGE_MAXIMUM, page: 1 },
  { clientId: 'publicApi' },
)

// Build user map from all available users
const userMap = computed(() => {
  const trustedContacts = result.value?.trustedContacts?.data ?? []
  const map = new Map<string, { label: string; value: string }>()

  trustedContacts.forEach((contact: any) => {
    if (contact.invitedByUser) {
      map.set(contact.invitedByUser.id, {
        label: contact.invitedByUser.name,
        value: contact.invitedByUser.id,
      })
    }
  })

  return map
})

// Internal model for the multi-select (array of { label, value, isAutomation? })
const internalSelection = computed({
  get() {
    if (!selectedInvitedBy.value) return []
    const options: Array<{ label: string; value: string; isAutomation?: boolean }> = []

    // Add automation option if includeAutomation is true
    if (selectedInvitedBy.value.includeAutomation) {
      options.push({ label: $t('Worksome System'), value: 'Worksome System', isAutomation: true })
    }

    // Add selected users
    selectedInvitedBy.value.users.forEach((userId) => {
      const user = userMap.value.get(userId)
      if (user) {
        options.push(user)
      }
    })

    return options
  },
  set(newSelection: Array<{ label: string; value: string; isAutomation?: boolean }>) {
    if (newSelection.length === 0) {
      selectedInvitedBy.value = null
      return
    }

    const users = newSelection.filter((item) => !item.isAutomation).map((item) => item.value)
    const includeAutomation = newSelection.some((item) => item.isAutomation)

    selectedInvitedBy.value = {
      users,
      includeAutomation,
    }
  },
})

const automationOption = {
  label: $t('Worksome System'),
  value: 'Worksome System',
  isAutomation: true,
}

const invitedByOptions = computed(() => {
  let options = [automationOption, ...Array.from(userMap.value.values())]

  // Filter by search if provided
  if (searchStr.value) {
    const search = searchStr.value.toLowerCase()
    options = options.filter((option) => option.label?.toLowerCase().includes(search))
  }

  // Sort by label
  return options.sort((a, b) => a.label.localeCompare(b.label))
})
</script>

<style scoped></style>
